/**
 * Global peg state management service
 * Ensures only one peg label shows at a time
 */
class PegService {
    constructor() {
      this.activeHotspotId = null;
      this.subscribers = new Map();
      this.hoverTimeout = null;
      this.debounceDelay = 50; // ms
    }
  
    /**
     * Register a peg component for state management
     * @param {string} id - Unique peg identifier
     * @param {Function} callback - State change callback
     */
    subscribe(id, callback) {
      this.subscribers.set(id, callback);
      
      return () => {
        this.subscribers.delete(id);
        if (this.activeHotspotId === id) {
          this.activeHotspotId = null;
        }
      };
    }
  
    /**
     * Set active hotspot with debouncing
     * @param {string} id - Peg identifier
     * @param {boolean} isHovered - Hover state
     */
    setActiveHotspot(id, isHovered) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
  
      if (isHovered) {
        this.hideAllExcept(id);
        
        this.activeHotspotId = id;
        this.notifySubscriber(id, true);
      } else {
        this.hoverTimeout = setTimeout(() => {
          if (this.activeHotspotId === id) {
            this.activeHotspotId = null;
            this.notifySubscriber(id, false);
          }
        }, this.debounceDelay);
      }
    }
  
    /**
     * Hide all hotspots except the specified one
     * @param {string} exceptId - ID to keep active
     */
    hideAllExcept(exceptId) {
      this.subscribers.forEach((callback, id) => {
        if (id !== exceptId) {
          callback(false);
        }
      });
    }
  
    /**
     * Notify a specific subscriber
     * @param {string} id - Subscriber ID
     * @param {boolean} isActive - Active state
     */
    notifySubscriber(id, isActive) {
      const callback = this.subscribers.get(id);
      if (callback) {
        callback(isActive);
      }
    }
  
    /**
     * Force hide all hotspots
     */
    hideAll() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
  
      this.activeHotspotId = null;
      this.subscribers.forEach((callback) => {
        callback(false);
      });
    }
  
    /**
     * Get current active hotspot ID
     * @returns {string|null}
     */
    getActiveHotspot() {
      return this.activeHotspotId;
    }
  
    /**
     * Check if a specific hotspot is active
     * @param {string} id - Hotspot ID
     * @returns {boolean}
     */
    isActive(id) {
      return this.activeHotspotId === id;
    }
  
    /**
     * Cleanup method for when service is destroyed
     */
    destroy() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      this.subscribers.clear();
      this.activeHotspotId = null;
    }
  }
  
  export const pegService = new PegService();
  
  if (process.env.NODE_ENV === 'development') {
    window.pegService = pegService;
  }